import React from 'react';
import { motion } from 'framer-motion';

const Hero = () => {
  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        duration: 0.8, 
        ease: [0.215, 0.61, 0.355, 1.0] // Cubic bezier for smooth entry
      } 
    }
  };

  const imageVariants = {
    hidden: { scale: 1.1, opacity: 0 },
    visible: { 
      scale: 1, 
      opacity: 1, 
      transition: { duration: 1.5, ease: "easeOut" } 
    }
  };

  return (
    <div className="bg-white min-h-screen font-sans">
      
      {/* Hero Section */}
      <section className="px-4 md:px-8 lg:px-12 pb-12">
        <div className="max-w-[95rem] mx-auto">
          
          {/* Hero Container */}
          <div className="relative rounded-[2.5rem] overflow-hidden min-h-[600px] lg:min-h-[85vh] shadow-sm group">
            
            {/* Background Image with Motion Entry */}
            <motion.div 
              className="absolute inset-0 bg-cover bg-center"
              style={{ 
                backgroundImage: 'url(/mun_hero.jpg)' 
              }}
              variants={imageVariants}
              initial="hidden"
              animate="visible"
              whileHover={{ scale: 1.05, transition: { duration: 0.7 } }} // Keep the hover effect
            />

            {/* Dark Overlay */}
            <div className="absolute inset-0 bg-slate-900/40"></div>

            {/* Content Container */}
            <div className="relative z-10 flex flex-col justify-center h-full min-h-[600px] lg:min-h-[85vh] p-8 md:p-16 lg:p-20">
              <motion.div 
                className="max-w-3xl flex flex-col items-center md:items-start"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                <motion.h1 
                  className="text-4xl md:text-6xl lg:text-8xl font-extrabold text-white mb-6 leading-[1.1] tracking-wide text-center md:text-left"
                  variants={itemVariants}
                >
                  A WORLD<br />
                  OF CHOICES
                </motion.h1>
                
                <motion.p 
                  className="text-gray-100 text-lg md:text-xl mb-10 max-w-2xl leading-relaxed font-light text-center md:text-left"
                  variants={itemVariants}
                >
                  En AV Global diseñamos experiencias educativas que conectan a los estudiantes con oportunidades internacionales reales: Programas MUN, doble titulación escolar y asesoría integral para acceso a educación superior en el extranjero.
                </motion.p>

                {/* Botones - Contenedor Flex para alinear uno al lado del otro */}
                <motion.div 
                  className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto"
                  variants={itemVariants}
                >
                  {/* Primary Button */}
                  <a href="https://calendly.com/avconsulting-info/30min?month=2026-03" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                    <motion.button 
                      className="bg-[#1D4D73] hover:bg-[#1a3b5c] w-full sm:w-auto text-white px-2 py-2 rounded-full flex items-center justify-between transition-all duration-300 shadow-lg text-lg group/btn border border-transparent"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span className='px-5 md:px-6 font-medium whitespace-nowrap'>Agendar Asesoría</span>
                      <div className="bg-white rounded-full p-3 text-[#1D4D73] group-hover/btn:scale-110 transition-transform flex-shrink-0">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M7 17L17 7" />
                            <path d="M7 7h10v10" />
                        </svg>
                      </div>
                    </motion.button>
                  </a>

                  {/* Secondary Button - Glassmorphism Style */}
                  <a href="https://www.linkedin.com/company/av-global-consulting/?viewAsMember=true" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                    <motion.button 
                      className="bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/40 w-full sm:w-auto text-white px-2 py-2 rounded-full flex items-center justify-between transition-all duration-300 shadow-lg text-lg group/btn2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span className='px-5 md:px-6 font-light whitespace-nowrap'>Actualízate con nosotros</span>
                      <div className="bg-white/20 rounded-full p-3 text-white group-hover/btn2:bg-white group-hover/btn2:text-[#0077b5] transition-all flex-shrink-0">
                        {/* Icono de LinkedIn para dar más contexto */}
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                          <rect x="2" y="9" width="4" height="12" />
                          <circle cx="4" cy="4" r="2" />
                        </svg>
                      </div>
                    </motion.button>
                  </a>
                </motion.div>

              </motion.div>
            </div>
          </div>

          {/* New Partnership Section */}
          <motion.div 
            className="mt-12 md:mt-16 flex flex-col sm:flex-row items-center justify-center gap-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <span className="text-gray-400 font-medium text-sm md:text-base tracking-widest uppercase">
              Partnership with:
            </span>
            <div className="flex items-center">
              <img 
                src="/hudson.webp" 
                alt="Hudson Global Scholars Logo" 
                className="h-12 md:h-16 object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-500 cursor-pointer"
              />
            </div>
          </motion.div>

        </div>
      </section>
    </div>
  );
};

export default Hero;